import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook to manage modal state synchronized with URL query parameters
 * @param {string} paramName - The query parameter name (e.g., 'upload-documents', 'kyc')
 * @param {boolean} initialValue - Initial modal state (default: false)
 * @returns {[boolean, function]} - [isOpen, setIsOpen] similar to useState
 */
export function useModalQueryParams(paramName, initialValue = false) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL on mount
  const [isOpen, setIsOpen] = useState(() => {
    const paramValue = searchParams.get(paramName);
    if (paramValue === 'true' || paramValue === '') {
      return true;
    }
    return initialValue;
  });

  // Sync state with URL when param changes
  useEffect(() => {
    const paramValue = searchParams.get(paramName);
    const shouldBeOpen = paramValue === 'true' || paramValue === '';

    if (paramValue !== null) {
      setIsOpen(prev => {
        // Only update if state needs to change
        if (prev !== shouldBeOpen) {
          return shouldBeOpen;
        }
        return prev;
      });
    }
  }, [searchParams, paramName]);

  // Update URL when modal state changes
  const updateModalState = (newState) => {
    const isOpening = Boolean(newState);
    setIsOpen(newState);

    const newSearchParams = new URLSearchParams(searchParams);
    if (isOpening) {
      // Add query param when opening
      newSearchParams.set(paramName, 'true');
    } else {
      // Remove query param when closing
      newSearchParams.delete(paramName);
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  return [isOpen, updateModalState];
}

